<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl mb-1">Settlements</h1>
    <Button variant="outline" @click="downloadCsv" :disabled="settlements.length === 0" title="Download CSV">
      <Download class="h-4 w-4" />
      Download
    </Button>
  </div>

  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <SettlementsControls
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="isLoading"
      :columns="settlementsTableColumns"
      :data="settlements"
      :total-count="totalCount"
      :page-number="pageNumber"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
    />
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import { LAYUP_API_GET_ITEMS_LIMIT } from '@/services/layupApi';
import { fetchSettlements, type SettlementsServiceGetReportRequest } from '@/services/settlements';
import { useGeneralStore } from '@/stores/general';
import { fetchAllPaginated, generateCsvBlobUrl } from '@/utils/export.utils';
import { currency, displayDate } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import { Download } from 'lucide-vue-next';
import type { DateRange } from 'radix-vue';
import { onMounted, ref } from 'vue';
import type { ModelSettlementReport } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import SettlementsControls from '../settlements-controls/SettlementsControls.vue';
import { settlementsTableColumns } from './SettlementsTableColumns';

const generalStore = useGeneralStore();

const isLoading = ref(true);
const settlementsParams = ref<SettlementsServiceGetReportRequest>({ limit: LAYUP_API_GET_ITEMS_LIMIT, sort: '-batch' });
const previousParams = ref<SettlementsServiceGetReportRequest>({ ...settlementsParams.value });
const settlements = ref<ModelSettlementReport[]>([]);

const totalCount = ref(1);
const pageNumber = ref(1);

onMounted(async () => onUpdateFilter(settlementsParams.value, true));

const onUpdateFilter = async (params: SettlementsServiceGetReportRequest, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  settlements.value = [];
  isLoading.value = true;

  try {
    params.limit = generalStore.pageSize;
    params.skip = (pageNumber.value - 1) * generalStore.pageSize;
    const response = await fetchSettlements(params);
    settlements.value = response.settlements ?? [];
    totalCount.value = response.totalCount || 0;

    addMetric('settlements-table', { status: 'success', channel: 'merchant', method: 'fetchSettlements' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settlements-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('settlements-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = (value: DateRange): void => {
  settlementsParams.value.fromDate = dayjs(`${value.start?.year}-${value.start?.month}-${value.start?.day}`)
    .startOf('day')
    .toISOString();
  settlementsParams.value.toDate = dayjs(`${value.end?.year}-${value.end?.month}-${value.end?.day}`)
    .endOf('day')
    .toISOString();

  onUpdateFilter(settlementsParams.value);
};

const onClearFilters = (): void => {
  settlementsParams.value = { limit: LAYUP_API_GET_ITEMS_LIMIT, sort: '-batch' };
  onUpdateFilter(settlementsParams.value);
};

const onSearchTermChange = (value: string): void => {
  settlementsParams.value.search = value.toUpperCase();
  onUpdateFilter(settlementsParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
  settlementsParams.value.skip = (pageNumber.value - 1) * generalStore.pageSize;
  onUpdateFilter(settlementsParams.value);
};

const onUpdatePageSize = (size: number) => {
  generalStore.pageSize = size;
  settlementsParams.value.limit = size;
  onUpdateFilter(settlementsParams.value);
};

const downloadCsv = async () => {
  const settlements = await fetchAllPaginated<ModelSettlementReport, SettlementsServiceGetReportRequest>(
    fetchSettlements,
    settlementsParams.value,
    'settlements',
  );
  if (settlements.length === 0) {
    console.warn('No data available for export');
    return;
  }

  const headerMap: { [Property in keyof ModelSettlementReport]: string } = {
    batch: 'Settlement Batch',
    reference: 'Settlement Reference',
    settledAt: 'Date Settled',
    amount: 'Settlement Amount',
    batchType: 'Batch Type',
  };
  const data = settlements.map((settlement) => {
    return {
      ...settlement,
      amount: currency(settlement.amount),
      settledAt: settlement.settledAt ? displayDate(settlement.settledAt) : '',
    };
  });

  const url = generateCsvBlobUrl(headerMap, data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `settlements-${dayjs().format('YYYY-MM-DD')}.csv`);
  link.click();
  URL.revokeObjectURL(url);
};
</script>
